import { useAtomValue } from "jotai";
import KeyValueItem from "src/components/general/KeyValueList/components/KeyValueItem/KeyValueItem";
import KeyValueList from "src/components/general/KeyValueList/KeyValueList";
import { useGlobalLocalContext } from "src/storage/localStorage";
import { refreshTokenClaimsAtom, tokenAtom } from "src/utils/auth/auth-atoms";
import type { AccessTokenClaims } from "src/utils/auth/auth-schema";

const debugMissing = "—";

const debugRoleLabel = (claims: AccessTokenClaims | null) => {
  const roles = [
    claims?.is_tenant_admin && "tenant admin",
    claims?.is_super_admin && "super admin",
    claims?.read_only && "read-only",
  ].filter(Boolean);
  return roles.length ? roles.join(" · ") : "user";
};

const formatTokenExp = (exp: number | undefined) => {
  if (exp == null) return debugMissing;
  const remainingMin = Math.round((exp * 1000 - Date.now()) / 60_000);
  const when = new Date(exp * 1000).toLocaleString();
  return remainingMin < 0 ? `${when} (expired)` : `${when} (${remainingMin}m)`;
};

export default function TopbarUserInfoDebug() {
  const claims = useAtomValue(tokenAtom);
  const refreshClaims = useAtomValue(refreshTokenClaimsAtom);
  const isDebugMode = useGlobalLocalContext((state) => state.isDebugMode);

  if (!isDebugMode) return null;

  const isImpersonatedSession =
    claims?.user_id != null &&
    refreshClaims?.user_id != null &&
    claims.user_id !== refreshClaims.user_id;

  return (
    <KeyValueList
      spread="narrow"
      spaceBetween
      className="text-xs mt-2 rounded bg-background-default p-1.5 -mx-2"
    >
      <KeyValueItem label="Role:">{debugRoleLabel(claims)}</KeyValueItem>
      <KeyValueItem label="User id:" withCopy>
        {claims?.user_id ?? debugMissing}
      </KeyValueItem>
      <KeyValueItem label="Tenant id:" withCopy>
        {claims?.tenant_id ?? debugMissing}
      </KeyValueItem>
      <KeyValueItem label="Token exp:">{formatTokenExp(claims?.exp)}</KeyValueItem>
      <KeyValueItem label="Impersonated:">
        {isImpersonatedSession ? `yes (${refreshClaims?.user_id})` : "no"}
      </KeyValueItem>
    </KeyValueList>
  );
}
